import { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbLabel } from '@store/breadcrumbLabels';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  Button,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import SummarizeOutlined from '@mui/icons-material/SummarizeOutlined';
import { useTheme } from '@mui/material/styles';
import HighchartsChart from '@components/charts/HighchartsChart';
import { tokenFor } from '@utils/chartTokens';
import { RuleTitle } from '@components/findings/RuleTitle';
import { nonceExportStyles } from '@theme/cspNonce';
import { organizationService } from '@services/organizationService';
import { UNRESOLVED_STATUSES } from '@services/findingsService';
import {
  reportingService,
  type IssuesBreakdownReport,
  type IssuesBreakdownExportFormat,
  type ExecutiveSummaryExportFormat,
  type Severity,
} from '@services/reportingService';

/** Blob text via FileReader, which (unlike Blob.text()) jsdom also implements. */
function readBlobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('could not read the export'));
    reader.readAsText(blob);
  });
}

const SEVERITY_ORDER: Severity[] = ['critical', 'major', 'minor', 'info'];
// REV-815: the second severity palette. It disagreed with HomePage's on three
// of four values — critical was #b71c1c here and #C62828 there — and two of
// these failed contrast on the surface they were painted on: #f9a825 measured
// 1.97:1 on white, #b71c1c 2.54:1 on the dark Paper. Both now come from the
// shared token table instead.

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h3" component="p">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

/**
 * REV-1163: a rule's title may hold Markdown code spans, shown as <code>. A
 * page title or URL is shown as it is — a backtick there is literal.
 */
const labelOf = (r: { label: string; isRuleTitle?: boolean }) =>
  r.isRuleTitle ? <RuleTitle text={r.label} /> : r.label;

function CountTable({
  caption,
  keyHeader,
  rows,
}: {
  caption: string;
  keyHeader: string;
  /**
   * `href` is an external destination (a scanned page URL) and opens in a new
   * tab. `to` is an in-app drill-through to the filtered findings list.
   *
   * REV-504: a row only gets `to` when the findings list can actually express
   * the filter. Severity and status can; WCAG level and rule cannot — the API
   * exposes `wcag_sc_id` (a single criterion) and no rule filter at all. Those
   * rows stay plain text rather than linking somewhere that silently ignores
   * the filter and shows every finding.
   */
  rows: Array<{ label: string; href?: string; to?: string; count: number; isRuleTitle?: boolean }>;
}) {
  const { t } = useTranslation();
  return (
    <Table size="small" aria-label={caption}>
      <caption style={{ textAlign: 'left', captionSide: 'top' }}>
        <Typography variant="h6" component="span">
          {caption}
        </Typography>
      </caption>
      <TableHead>
        <TableRow>
          <TableCell scope="col">{keyHeader}</TableCell>
          {/* textAlign via sx, not align="right" — the align prop emits
              flex-direction:row-reverse, which our own engine flags (STRUCTURE-03). */}
          <TableCell scope="col" sx={{ textAlign: 'right' }}>
            {t('reports.issuesBreakdown.colCount')}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={`${r.label}-${i}`}>
            <TableCell component="th" scope="row">
              {r.to ? (
                <Link component={RouterLink} to={r.to}>
                  {labelOf(r)}
                </Link>
              ) : r.href ? (
                <Link href={r.href} target="_blank" rel="noopener noreferrer">
                  {labelOf(r)}
                </Link>
              ) : (
                labelOf(r)
              )}
            </TableCell>
            <TableCell sx={{ textAlign: 'right' }}>{r.count}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function IssuesBreakdownReportPage() {
  const { t } = useTranslation();
  const mode = useTheme().palette.mode;
  const { slug, contextId } = useParams<{ slug: string; contextId: string }>();

  const [report, setReport] = useState<IssuesBreakdownReport | null>(null);
  // REV-939: the tracked-place crumb above this report names the place — but
  // only the place loaded for the URL now on screen (`crumbLoadedFor`); the
  // page stays mounted when only :contextId changes.
  const [crumbLoadedFor, setCrumbLoadedFor] = useState<string | null>(null);
  useBreadcrumbLabel(
    slug && contextId ? `/orgs/${slug}/contexts/${contextId}` : undefined,
    report && crumbLoadedFor ? { path: crumbLoadedFor, label: report.context.name } : null
  );
  const [orgId, setOrgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug || !contextId) return;
    setLoading(true);
    setError(null);
    try {
      const org = await organizationService.getBySlug(slug);
      setOrgId(org.id);
      const data = await reportingService.getIssuesBreakdown(org.id, contextId);
      setReport(data);
      setCrumbLoadedFor(`/orgs/${slug}/contexts/${contextId}`);
    } catch {
      setError(t('reports.issuesBreakdown.loadError'));
    } finally {
      setLoading(false);
    }
  }, [slug, contextId, t]);

  // Shared blob-delivery for every report export: DOCX downloads, HTML opens in
  // a new tab. The blob is Bearer-authenticated by the axios instance, so the
  // new tab needs no auth of its own.
  //
  // REV-803: a blob: tab inherits THIS page's enforced Content-Security-Policy,
  // which blocks the export's own <style> unless it carries this page's nonce,
  // so the HTML is re-wrapped with its styles nonced (scripts stay blocked).
  const deliverExport = useCallback(
    async (blob: Blob, format: IssuesBreakdownExportFormat, filenameBase: string) => {
      if (format === 'html') {
        const html = nonceExportStyles(await readBlobText(blob));
        const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        window.open(url, '_blank', 'noopener');
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        return;
      }
      // DOCX and CSV (REV-816) both download; the blob already carries its own
      // Content-Type from the API, so only the filename extension differs.
      const ext = format === 'csv' ? 'csv' : 'docx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filenameBase}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    []
  );

  const handleExport = useCallback(
    async (format: IssuesBreakdownExportFormat) => {
      setExportAnchor(null);
      if (!orgId || !contextId) return;
      setExporting(true);
      setExportError(null);
      try {
        const blob = await reportingService.getIssuesBreakdownExport(orgId, contextId, format);
        await deliverExport(blob, format, `issues-breakdown-${contextId}`);
      } catch {
        setExportError(t('reports.issuesBreakdown.exportError'));
      } finally {
        setExporting(false);
      }
    },
    [orgId, contextId, t, deliverExport]
  );

  const handleExecSummaryExport = useCallback(
    async (format: ExecutiveSummaryExportFormat) => {
      setExportAnchor(null);
      if (!orgId || !contextId) return;
      setExporting(true);
      setExportError(null);
      try {
        const blob = await reportingService.getExecutiveSummaryExport(orgId, contextId, format);
        await deliverExport(blob, format, `executive-summary-${contextId}`);
      } catch {
        setExportError(t('reports.issuesBreakdown.exportError'));
      } finally {
        setExporting(false);
      }
    },
    [orgId, contextId, t, deliverExport]
  );

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label={t('common.loading')} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          component={RouterLink}
          to={`/orgs/${slug}/contexts/${contextId}`}
        >
          {t('reports.issuesBreakdown.back')}
        </Button>
      </Box>
    );
  }

  if (!report) return null;

  const sev = report.by_severity;
  const severitySummary = SEVERITY_ORDER.map((s) => `${t(`reports.severity.${s}`)} ${sev[s]}`).join(
    ', '
  );

  const severityChartOptions: Highcharts.Options = {
    chart: { type: 'column', height: 280 },
    title: { text: undefined },
    xAxis: { categories: SEVERITY_ORDER.map((s) => t(`reports.severity.${s}`)) },
    yAxis: { title: { text: t('reports.issuesBreakdown.colCount') }, allowDecimals: false },
    legend: { enabled: false },
    series: [
      {
        type: 'column',
        name: t('reports.issuesBreakdown.colCount'),
        data: SEVERITY_ORDER.map((s) => ({ y: sev[s], color: tokenFor('severity', s, mode).fill })),
      },
    ],
  };

  const wcagLevels: Array<keyof IssuesBreakdownReport['by_wcag_level']> = [
    'A',
    'AA',
    'AAA',
    'unspecified',
  ];

  const wcagLabel = (lvl: (typeof wcagLevels)[number]) =>
    lvl === 'unspecified' ? t('reports.issuesBreakdown.unspecified') : lvl;

  // REV-504: WCAG level was data-only while severity had a chart. Same column
  // shape so the two panels read as a pair.
  const wcagChartOptions: Highcharts.Options = {
    chart: { type: 'column', height: 280 },
    title: { text: undefined },
    xAxis: { categories: wcagLevels.map(wcagLabel) },
    yAxis: { title: { text: t('reports.issuesBreakdown.colCount') }, allowDecimals: false },
    legend: { enabled: false },
    series: [
      {
        type: 'column',
        name: t('reports.issuesBreakdown.colCount'),
        data: wcagLevels.map((lvl) => report.by_wcag_level[lvl]),
      },
    ],
  };

  const wcagSummary = wcagLevels
    .map((lvl) => `${wcagLabel(lvl)}: ${report.by_wcag_level[lvl]}`)
    .join(', ');

  /**
   * Drill-through into the org findings list, scoped to this tracked place.
   *
   * REV-548: the defaults matter as much as the params. This report counts AUTO
   * findings in open|acknowledged|in_progress, while the findings list defaults
   * to BOTH sources and "anything but resolved" — which also counts
   * false_positive and wont_fix. Without pinning source and status, a row
   * reading "AA: 12" links to a list showing more than 12, and the link
   * contradicts the number that produced it.
   *
   * `...params` is spread last so a row with its own status (the By status
   * table, which counts every status including resolved) keeps it.
   */
  const findingsHref = (params: Record<string, string>) =>
    `/orgs/${slug}/findings?${new URLSearchParams({
      context_id: contextId ?? '',
      source: 'auto',
      status: UNRESOLVED_STATUSES,
      ...params,
    }).toString()}`;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          gap: 1,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          component={RouterLink}
          to={`/orgs/${slug}/contexts/${contextId}`}
        >
          {t('reports.issuesBreakdown.back')}
        </Button>

        <Box>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlined />}
            endIcon={<ExpandMore />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            disabled={exporting}
            aria-haspopup="menu"
            // A menu-button must always expose its collapsed/expanded state, so
            // aria-expanded is present as "false" when closed (not omitted).
            // aria-controls stays conditional — the menu is only in the DOM when
            // open, so referencing its id while closed would be a dangling ref.
            aria-expanded={exportAnchor ? 'true' : 'false'}
            aria-controls={exportAnchor ? 'issues-breakdown-export-menu' : undefined}
          >
            {exporting
              ? t('reports.issuesBreakdown.exporting')
              : t('reports.issuesBreakdown.export')}
          </Button>
          <Menu
            id="issues-breakdown-export-menu"
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
            MenuListProps={{ 'aria-label': t('reports.issuesBreakdown.exportMenuLabel') }}
          >
            <MenuItem onClick={() => handleExport('docx')}>
              <ListItemIcon>
                <DescriptionOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('reports.issuesBreakdown.exportDocx')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExport('html')}>
              <ListItemIcon>
                <PrintOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('reports.issuesBreakdown.exportHtml')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExport('csv')}>
              <ListItemIcon>
                <TableChartOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('reports.issuesBreakdown.exportCsv')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleExecSummaryExport('docx')}>
              <ListItemIcon>
                <SummarizeOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('reports.issuesBreakdown.exportExecSummaryDocx')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExecSummaryExport('html')}>
              <ListItemIcon>
                <SummarizeOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('reports.issuesBreakdown.exportExecSummaryHtml')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}

      <Typography variant="h4" component="h1" gutterBottom>
        {t('reports.issuesBreakdown.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('reports.issuesBreakdown.subtitle', { context: report.context.name })}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard label={t('reports.issuesBreakdown.openTotal')} value={report.totals.open} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label={t('reports.issuesBreakdown.allTotal')} value={report.totals.all} />
        </Grid>
      </Grid>

      {report.totals.all === 0 ? (
        <Alert severity="info">{t('reports.issuesBreakdown.empty')}</Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Severity: chart + accessible data table (the table is the source of truth). */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                {t('reports.issuesBreakdown.bySeverity')}
              </Typography>
              <HighchartsChart
                options={severityChartOptions}
                accessibleName={t('reports.issuesBreakdown.bySeverity')}
                description={t('reports.issuesBreakdown.severityChartDescription', {
                  summary: severitySummary,
                })}
              />
              <CountTable
                caption={t('reports.issuesBreakdown.bySeverity')}
                keyHeader={t('reports.issuesBreakdown.colSeverity')}
                rows={SEVERITY_ORDER.map((s) => ({
                  label: t(`reports.severity.${s}`),
                  to: findingsHref({ severity: s }),
                  count: sev[s],
                }))}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                {t('reports.issuesBreakdown.byWcagLevel')}
              </Typography>
              <HighchartsChart
                options={wcagChartOptions}
                accessibleName={t('reports.issuesBreakdown.byWcagLevel')}
                description={t('reports.issuesBreakdown.wcagChartDescription', {
                  summary: wcagSummary,
                })}
              />
              <CountTable
                caption={t('reports.issuesBreakdown.byWcagLevel')}
                keyHeader={t('reports.issuesBreakdown.colLevel')}
                rows={wcagLevels.map((lvl) => ({
                  label: wcagLabel(lvl),
                  count: report.by_wcag_level[lvl],
                  to: findingsHref({ wcag_level: lvl }),
                }))}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <CountTable
                caption={t('reports.issuesBreakdown.byStatus')}
                keyHeader={t('reports.issuesBreakdown.colStatus')}
                rows={Object.entries(report.by_status).map(([status, count]) => ({
                  label: status,
                  to: findingsHref({ status }),
                  count,
                }))}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <CountTable
                caption={t('reports.issuesBreakdown.topPages')}
                keyHeader={t('reports.issuesBreakdown.colPage')}
                rows={report.top_pages.map((p) => ({
                  label: p.title || p.url || '—',
                  href: p.url || undefined,
                  count: p.count,
                }))}
              />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <CountTable
                caption={t('reports.issuesBreakdown.topRules')}
                keyHeader={t('reports.issuesBreakdown.colRule')}
                rows={report.top_rules.map((r) => ({
                  label: r.name || r.rule_id || '—',
                  isRuleTitle: true,
                  count: r.count,
                  // A definition with no rule_id cannot be filtered on, so that
                  // row stays plain text rather than linking to everything.
                  ...(r.rule_id ? { to: findingsHref({ rule_id: r.rule_id }) } : {}),
                }))}
              />
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default IssuesBreakdownReportPage;
